import { css } from "@compiled/react";

import { token } from "@atlaskit/tokens";

import { Expand } from "@/libraries/design/components/expand/src/expand";
import { wacToken } from "@/libraries/design/foundations";

import {
    SideNavLink,
    SideNavLinkProps
} from "../../side-nav-link/src/side-nav-link";
import { getCurrRoute } from "../../utils";

export interface SideNavCollectionProps {
    type: "collection";
    collectionName: string;
    depth?: number;
    sideNavItems: (SideNavCollectionProps | SideNavLinkProps)[];
}

const MAX_DEPTH = 2;

export const SideNavCollection = ({
    collectionName,
    depth = 0,
    sideNavItems
}: SideNavCollectionProps) => {
    // Recursively check if any child link (including in nested collections) is active
    const currRoute = getCurrRoute();
    const checkItems = (
        items: (SideNavCollectionProps | SideNavLinkProps)[]
    ): boolean => {
        return items.some((item) => {
            if (item.type === "link") {
                return currRoute === item.href;
            }
            if (item.type === "collection") {
                return checkItems(item.sideNavItems);
            }
            return false;
        });
    };

    const isExpanded = checkItems(sideNavItems);
    if (depth >= MAX_DEPTH) return null;

    return (
        <li
            key={collectionName}
            css={[
                listItemCss,
                css({
                    "paddingBlockStart":
                        depth === 0 ? token("space.200") : token("space.100"),
                    "paddingBlockEnd":
                        depth === 0 ? token("space.200") : token("space.050"),

                    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
                    "> div": {
                        gap: depth === 0 ? 0 : token("space.050")
                    }
                })
            ]}
        >
            <Expand
                label={
                    <span
                        className={depth === 0 ? "h6" : "body-md"}
                        style={{
                            fontFamily: wacToken("font.charlie.text")
                        }}
                    >
                        {collectionName}
                    </span>
                }
                isExpanded={isExpanded}
            >
                <ul css={listStyleNoneCss}>
                    {sideNavItems.map((sideNavItem) => {
                        if (sideNavItem.type === "collection") {
                            return (
                                <SideNavCollection
                                    key={sideNavItem.collectionName}
                                    type="collection"
                                    collectionName={sideNavItem.collectionName}
                                    sideNavItems={sideNavItem.sideNavItems}
                                    depth={depth + 1}
                                />
                            );
                        }
                        if (sideNavItem.type === "link") {
                            return (
                                <SideNavLink
                                    key={sideNavItem.text}
                                    type="link"
                                    text={sideNavItem.text}
                                    href={sideNavItem.href}
                                    anchors={sideNavItem.anchors}
                                    depth={depth + 1}
                                />
                            );
                        }
                        return null;
                    })}
                </ul>
            </Expand>
        </li>
    );
};

const listStyleNoneCss = css({
    listStyle: "none",
    margin: token("space.0"),
    padding: token("space.0")
});

const listItemCss = css({
    "listStyle": "none",
    "display": "flex",
    "alignItems": "center",

    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    "> div > button > div > p": {
        marginBlockStart: 0
    }
});
